import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import AdminLayout from "@/components/layout/AdminLayout";
import SalesmanLayout from "@/components/layout/SalesmanLayout";
import Dashboard from "@/pages/admin/Dashboard";
import Salesmen from "@/pages/admin/Salesmen";
import SalesmanDetail from "@/pages/admin/SalesmanDetail";
import Products from "@/pages/admin/Products";
import StockIssue from "@/pages/admin/StockIssue";
import Requests from "@/pages/admin/Requests";
import Adjustments from "@/pages/admin/Adjustments";
import Closures from "@/pages/admin/Closures";
import DayDetail from "@/pages/admin/DayDetail";
import Collections from "@/pages/admin/Collections";
import Receivables from "@/pages/admin/Receivables";
import Expenses from "@/pages/admin/Expenses";
import Reports from "@/pages/admin/Reports";
import AuditLogs from "@/pages/admin/AuditLogs";
import SettingsPage from "@/pages/admin/Settings";
import Today from "@/pages/salesman/Today";
import MyStock from "@/pages/salesman/MyStock";
import CloseDay from "@/pages/salesman/CloseDay";
import SCollections from "@/pages/salesman/Collections";
import SExpenses from "@/pages/salesman/Expenses";
import SRequests from "@/pages/salesman/Requests";
import SHistory, { HistoryDay } from "@/pages/salesman/History";
import Profile from "@/pages/salesman/Profile";
import Inventory from "@/pages/v2/Inventory";
import Customers from "@/pages/v2/Customers";
import Wholesale from "@/pages/v2/Wholesale";
import Credit from "@/pages/v2/Credit";
import Payments from "@/pages/v2/Payments";
import Cashbook from "@/pages/v2/Cashbook";
import Print from "@/pages/v2/Print";
import Attendance from "@/pages/admin/V3";
import { WLayout, WHome, WOrder, WOrders, WCollect } from "@/pages/w/WPortal";

export const homeFor = (u) => (u.role === "admin" ? "/admin" : u.role === "wholesale" ? "/w" : "/s");

function Guard({ role, children }) {
  const { loading, user } = useAuth();
  if (loading) return <div className="p-10 text-center text-sm text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== role) return <Navigate to={homeFor(user)} replace />;
  return children;
}

function Home() {
  const { loading, user } = useAuth();
  if (loading) return null;
  return <Navigate to={!user ? "/login" : homeFor(user)} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/admin" element={<Guard role="admin"><AdminLayout /></Guard>}>
            <Route index element={<Dashboard />} />
            <Route path="salesmen" element={<Salesmen />} />
            <Route path="salesmen/:id" element={<SalesmanDetail />} />
            <Route path="products" element={<Products />} />
            <Route path="issue" element={<StockIssue />} />
            <Route path="transfers" element={<Requests kind="transfers" />} />
            <Route path="returns" element={<Requests kind="returns" />} />
            <Route path="adjustments" element={<Adjustments />} />
            <Route path="closures" element={<Closures />} />
            <Route path="day/:sid/:date" element={<DayDetail />} />
            <Route path="collections" element={<Collections />} />
            <Route path="receivables" element={<Receivables />} />
            <Route path="expenses" element={<Expenses />} />
            <Route path="reports" element={<Reports />} />
            <Route path="audit" element={<AuditLogs />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="attendance" element={<Attendance />} />
            <Route path="inventory" element={<Inventory />} />
            <Route path="customers" element={<Customers />} />
            <Route path="wholesale" element={<Wholesale />} />
            <Route path="credit" element={<Credit />} />
            <Route path="payments" element={<Payments />} />
            <Route path="cashbook" element={<Cashbook />} />
          </Route>
          <Route path="/s" element={<Guard role="salesman"><SalesmanLayout /></Guard>}>
            <Route index element={<Today />} />
            <Route path="stock" element={<MyStock />} />
            <Route path="close" element={<CloseDay />} />
            <Route path="collections" element={<SCollections />} />
            <Route path="expenses" element={<SExpenses />} />
            <Route path="transfers" element={<SRequests kind="transfers" />} />
            <Route path="returns" element={<SRequests kind="returns" />} />
            <Route path="history" element={<SHistory />} />
            <Route path="history/:date" element={<HistoryDay />} />
            <Route path="profile" element={<Profile />} />
          </Route>
          <Route path="/w" element={<Guard role="wholesale"><WLayout /></Guard>}>
            <Route index element={<WHome />} />
            <Route path="order" element={<WOrder />} />
            <Route path="orders" element={<WOrders />} />
            <Route path="collect" element={<WCollect />} />
          </Route>
          <Route path="/print/:kind" element={<Guard role="admin"><Print /></Guard>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster richColors position="top-center" />
    </AuthProvider>
  );
}
